import VueWrapper from '@/components/core/Vue/vue.wrapper';
import {CAP_DIRECTION} from '@/globals';
import {CreateEditEquipmentCapViewModel, EquipmentCapModel} from '@/sdk';
import {EquipmentCapsApi} from '@/sdk/api-services/equipment-caps.api';
import {CoreService} from '@/services/core.service';
import {Component, Prop, Watch} from 'vue-property-decorator';

interface SelectCapDialogData {
    capType: 'in' | 'out';
    availableCaps?: EquipmentCapModel[]; // Made optional since caps now come from props
    currentCapId?: string | null;
    cableId?: string;
    coreId?: string | null;
    locationId?: string; // Add locationId for creating new caps
}

@Component({
    name: 'SelectCapDialogComponent',
})
export default class SelectCapDialogComponent extends VueWrapper {
    public readonly name = 'SelectCapDialogComponent';

    @Prop({
        type: Array,
        default: () => [],
    })
    public readonly availableInCaps!: EquipmentCapModel[];

    @Prop({
        type: Array,
        default: () => [],
    })
    public readonly availableOutCaps!: EquipmentCapModel[];

    public selectedCapId: string | null = null;
    public capType: 'in' | 'out' = 'in';
    public availableCaps: EquipmentCapModel[] = [];
    public cableId: string | null = null;
    public coreId: string | null = null;
    public locationId: string | null = null;

    // Add cap form state
    public showAddCapForm: boolean = false;
    // public isAddingCap: boolean = false;
    public isDeletingCap: boolean = false;

    // Form for adding new cap
    // public newCapForm: CreateEditEquipmentCapViewModel = {
    //     title: '',
    //     capDirection: CAP_DIRECTION.IN,
    //     sortOrder: 0,
    //     cableEntryType: '',
    //     modelNo: '',
    //     certificationNo: '',
    //     manufacturer: '',
    //     serialNo: '',
    //     protectionType: '',
    // };

    public __selectCapDialogData: SelectCapDialogData | null = null;

    // Equipment Caps API instance
    private equipmentCapsApi = new EquipmentCapsApi();

    @Watch('availableInCaps')
    @Watch('availableOutCaps')
    @Watch('capType')
    private updateAvailableCaps(): void {
        this.availableCaps = this.capType === 'in' ? this.availableInCaps : this.availableOutCaps;
    }

    public get dialogTitle(): string {
        return `Select ${this.capType === 'in' ? 'Input' : 'Output'} Cap`;
    }

    public mounted(): void {
        // Initialize available caps based on default capType
        this.updateAvailableCaps();
    }

    private initializeDialogData(): void {
        // Get dialog data from a temporary storage or emit event to parent
        const dialogData = this.getDialogData();
        if (dialogData) {
            this.capType = dialogData.capType;
            // Use props instead of dialog data for available caps
            this.availableCaps = this.capType === 'in' ? this.availableInCaps : this.availableOutCaps;
            this.selectedCapId = dialogData.currentCapId || null;
            this.cableId = dialogData.cableId || null;
            this.coreId = dialogData.coreId || null;
            this.locationId = dialogData.locationId || null;
        }
    }

    private getDialogData(): SelectCapDialogData | null {
        // This would typically be retrieved from a service or parent component
        // For now, we'll use a simple window property approach
        return this.__selectCapDialogData || null;
    }

    public selectCap(cap: EquipmentCapModel): void {
        this.selectedCapId = cap.id!;
    }

    // public showAddForm(): void {
    //     this.showAddCapForm = true;
    //     this.resetAddForm();
    // }

    // public hideAddForm(): void {
    //     this.showAddCapForm = false;
    //     this.resetAddForm();
    // }

    public async addSingleCap(): Promise<void> {
        if (!this.locationId) {
            this.AlertSrv.show('error', 'Location ID is required to create a new cap.');
            return;
        }
        const random = Math.floor(Math.random() * 16777215).toString(16);
        const newCap = new CreateEditEquipmentCapViewModel({
            title: `Cap ${random}`,
            capDirection: this.capType === 'in' ? CAP_DIRECTION.IN : CAP_DIRECTION.OUT,
            sortOrder: 0,
        });

        try {
            // this.isAddingCap = true;

            // Get the highest sort order for after parameter
            const afterOrder = this.getHighestSortOrder();

            // Create the cap using the API
            const response = await this.equipmentCapsApi.createCapsMultiple(this.locationId, afterOrder, [newCap]);

            if (response && response.length > 0) {
                const newCap = response[0];

                // Add the new cap to available caps
                // this.availableCaps.push(newCap);

                // Sort caps by sort order
                this.sortCapsBySortOrder();

                // Auto-select the newly created cap
                // this.selectedCapId = newCap.id!;

                this.AlertSrv.show('success', 'Cap created successfully!');
                // this.hideAddForm();

                // Emit event to notify parent about the new cap
                // this.$emit('cap-added', newCap);
                this.$root.$emit('cap-added', newCap);
            }
        } catch (error: any) {
            console.error('Error creating cap:', error);
            this.AlertSrv.show('error', error?.message || 'Failed to create cap. Please try again.');
        } finally {
            // this.isAddingCap = false;
        }
    }

    public async deleteCap(cap: EquipmentCapModel): Promise<void> {
        if (!cap.id) {
            return;
        }

        // Show confirmation dialog
        const confirmed = await this.ConfirmSrv.open('Delete Cap', `Are you sure you want to delete the cap "${cap.title}"? This action cannot be undone.`);

        if (!confirmed) {
            return;
        }

        try {
            this.isDeletingCap = true;

            // Delete the cap using the API
            await this.equipmentCapsApi.deleteCap(cap.id);

            // Remove the cap from available caps
            const index = this.availableCaps.findIndex(c => c.id === cap.id);
            if (index > -1) {
                this.availableCaps.splice(index, 1);
            }

            // Clear selection if the deleted cap was selected
            if (this.selectedCapId === cap.id) {
                this.selectedCapId = null;
            }

            this.AlertSrv.show('success', 'Cap deleted successfully!');

            // Emit event to notify parent about the deletion
            this.$emit('cap-deleted', cap);
            this.$root.$emit('cap-deleted', cap);
        } catch (error: any) {
            console.error('Error deleting cap:', error);
            this.AlertSrv.show('error', error?.message || 'Failed to delete cap. Please try again.');
        } finally {
            this.isDeletingCap = false;
        }
    }

    // Helper methods
    private resetAddForm(): void {
        //
    }

    private getNextSortOrder(): number {
        if (this.availableCaps.length === 0) {
            return 1;
        }
        const maxOrder = Math.max(...this.availableCaps.map(cap => cap.sortOrder || 0));
        return maxOrder + 1;
    }

    private getHighestSortOrder(): number {
        if (this.availableCaps.length === 0) {
            return 0;
        }
        return Math.max(...this.availableCaps.map(cap => cap.sortOrder || 0));
    }

    private sortCapsBySortOrder(): void {
        this.availableCaps.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    }

    public confirmSelection(): void {
        if (this.selectedCapId) {
            const selectedCap = this.availableCaps.find(cap => cap.id === this.selectedCapId);
            if (selectedCap) {
                this.$emit('cap-selected', {
                    cap: selectedCap,
                    capType: this.capType,
                    cableId: this.cableId,
                    coreId: this.coreId,
                });

                // Emit to global event bus for parent component communication
                this.$root.$emit('cap-selected', {
                    cap: selectedCap,
                    capType: this.capType,
                    cableId: this.cableId,
                    coreId: this.coreId,
                });
            }
        }
        this.closeDialog();
    }

    public closeDialog(): void {
        this.CoreSrv.CloseModal(this.name);
        this.resetDialog();
    }

    private resetDialog(): void {
        this.selectedCapId = null;
        this.capType = 'in';
        this.availableCaps = [];
        this.cableId = null;
        this.coreId = null;
        this.locationId = null;
        this.showAddCapForm = false;
        // this.isAddingCap = false;
        this.isDeletingCap = false;

        // Clear dialog data
        this.__selectCapDialogData = null;

        // Reset add cap form
        this.resetAddForm();
    }

    // Static method to open dialog with data
    public openDialog(data: SelectCapDialogData): void {
        // Store dialog data globally for the component to access
        this.__selectCapDialogData = data;

        // Use CoreService to open the dialog
        const coreService = new CoreService();
        coreService.OpenModal(this.name);

        this.initializeDialogData();
    }
}

// Export the interface and static method for external use
export {SelectCapDialogComponent, SelectCapDialogData};
